import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import SpeedIcon from '@mui/icons-material/Speed';

interface SystemMetric {
  name: string;
  value: string;
  status: 'healthy' | 'warning' | 'critical';
  icon: React.ReactNode;
}

const mockMetrics: SystemMetric[] = [
  {
    name: 'CPU Usage',
    value: '45%',
    status: 'healthy',
    icon: <MemoryIcon />,
  },
  {
    name: 'Memory Usage',
    value: '78%',
    status: 'warning',
    icon: <StorageIcon />,
  },
  {
    name: 'Response Time',
    value: '120ms',
    status: 'healthy',
    icon: <SpeedIcon />,
  },
];

const mockAlerts = [
  {
    severity: 'warning',
    message: 'High memory usage detected',
    timestamp: '10 minutes ago',
  },
  {
    severity: 'error',
    message: 'API endpoint /users/auth failed health check',
    timestamp: '15 minutes ago',
  },
  {
    severity: 'info',
    message: 'System backup completed successfully',
    timestamp: '1 hour ago',
  },
];

export default function AdminDashboard() {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getAlertIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <WarningIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
        return <CheckCircleIcon color="info" />;
      default:
        return <CheckCircleIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <DashboardIcon sx={{ fontSize: 32, mr: 2 }} />
        <Typography variant="h4">Admin Dashboard</Typography>
      </Box>

      <Grid container spacing={3}>
        {mockMetrics.map((metric, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {metric.icon}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {metric.name}
                  </Typography>
                </Box>
                <Typography variant="h4" component="div">
                  {metric.value}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={metric.status.toUpperCase()}
                    color={getStatusColor(metric.status) as any}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">System Alerts</Typography>
              <Button variant="contained" color="primary">
                Clear All
              </Button>
            </Box>
            <List>
              {mockAlerts.map((alert, index) => (
                <ListItem key={index} divider={index !== mockAlerts.length - 1}>
                  <ListItemIcon>
                    {getAlertIcon(alert.severity)}
                  </ListItemIcon>
                  <ListItemText
                    primary={alert.message}
                    secondary={alert.timestamp}
                  />
                  <Chip
                    label={alert.severity.toUpperCase()}
                    color={alert.severity === 'error' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
} 